import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { slidersAPI, categoriesAPI, productsAPI, bannersAPI } from '../services/api';
import ProductCard from '../components/ProductCard';
import { HiArrowRight } from 'react-icons/hi';

const Home = () => {
  const { t } = useTranslation();
  const [sliders, setSliders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [slidersRes, categoriesRes, featuredRes, newRes, bestRes, bannersRes] = await Promise.all([
        slidersAPI.getActive(),
        categoriesAPI.getFeatured(),
        productsAPI.getFeatured(),
        productsAPI.getNewArrivals(),
        productsAPI.getBestSellers(),
        bannersAPI.getByPosition('home-middle')
      ]);

      setSliders(slidersRes.data.sliders);
      setCategories(categoriesRes.data.categories);
      setFeaturedProducts(featuredRes.data.products);
      setNewArrivals(newRes.data.products);
      setBestSellers(bestRes.data.products);
      setBanners(bannersRes.data.banners);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Hero Slider */}
      <section className="relative">
        <Swiper
          modules={[Autoplay, Pagination, EffectFade]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          loop={true}
          className="h-[60vh] md:h-[80vh]"
        >
          {sliders.map((slide) => (
            <SwiperSlide key={slide._id}>
              <div className="relative w-full h-full">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                <div 
                  className="absolute inset-0"
                  style={{ backgroundColor: `rgba(0,0,0,${slide.overlayOpacity / 100})` }}
                />
                <div className={`absolute inset-0 flex items-center ${
                  slide.textPosition === 'center' ? 'justify-center text-center' :
                  slide.textPosition === 'right' ? 'justify-end text-right' : 'justify-start text-left'
                }`}>
                  <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
                    <div className={`max-w-xl ${slide.textPosition === 'center' ? 'mx-auto' : ''}`}>
                      <p className="text-sm md:text-base uppercase tracking-[0.3em] mb-4 animate-slide-up" style={{ color: slide.textColor }}>
                        {slide.subtitle}
                      </p>
                      <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-semibold mb-6 animate-slide-up" style={{ color: slide.textColor, animationDelay: '0.1s' }}>
                        {slide.title}
                      </h2>
                      <p className="text-lg md:text-xl mb-8 opacity-80 animate-slide-up" style={{ color: slide.textColor, animationDelay: '0.2s' }}>
                        {slide.description}
                      </p>
                      <Link
                        to={slide.buttonLink}
                        className="inline-block bg-luxury-charcoal text-white px-10 py-4 text-sm uppercase tracking-widest hover:bg-gray-800 transition-all duration-300 animate-slide-up"
                        style={{ animationDelay: '0.3s' }}
                      >
                        {slide.buttonText}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Features Bar */}
      <section className="bg-white py-8 border-b border-primary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-500">{t('home.freeShipping')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('home.freeShippingDesc')}</p>
            </div>
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-500">{t('home.easyReturns')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('home.easyReturnsDesc')}</p>
            </div>
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-500">{t('home.securePayment')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('home.securePaymentDesc')}</p>
            </div>
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-500">{t('home.premiumQuality')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('home.premiumQualityDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl mb-4">{t('home.shopByCategory')}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {t('home.ourCollections')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {categories.map((category) => (
              <Link
                key={category._id}
                to={`/shop?category=${category.slug}`}
                className="group relative aspect-[3/4] overflow-hidden"
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-end p-6">
                  <h3 className="text-white font-serif text-xl md:text-2xl mb-2">{category.name}</h3>
                  <span className="text-white/80 text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Explore
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl mb-2">{t('home.featuredProducts')}</h2>
              <p className="text-gray-500">{t('home.heroSubtitle')}</p>
            </div>
            <Link
              to="/shop?featured=true"
              className="hidden md:flex items-center space-x-2 text-sm uppercase tracking-widest hover:text-luxury-gold transition-colors"
            >
              <span>{t('common.viewAll')}</span>
              <HiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {featuredProducts.slice(0, 8).map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>

          <div className="mt-10 text-center md:hidden">
            <Link
              to="/shop?featured=true"
              className="inline-flex items-center space-x-2 text-sm uppercase tracking-widest hover:text-luxury-gold transition-colors"
            >
              <span>{t('common.viewAll')}</span>
              <HiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Promotional Banners */}
      {banners.length > 0 && (
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {banners.slice(0, 2).map((banner) => (
                <Link
                  key={banner._id}
                  to={banner.link}
                  className="group relative aspect-[16/9] md:aspect-[16/7] overflow-hidden"
                >
                  <img
                    src={banner.image}
                    alt={banner.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6">
                    <p className="text-sm uppercase tracking-widest mb-2">{banner.subtitle}</p>
                    <h3 className="font-serif text-2xl md:text-3xl">{banner.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* New Arrivals */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl mb-2">{t('home.newArrivals')}</h2>
              <p className="text-gray-500">{t('home.heroSubtitle')}</p>
            </div>
            <Link
              to="/shop?newArrival=true"
              className="hidden md:flex items-center space-x-2 text-sm uppercase tracking-widest hover:text-luxury-gold transition-colors"
            >
              <span>{t('common.viewAll')}</span>
              <HiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {newArrivals.slice(0, 4).map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Section */}
      <section className="py-20 bg-primary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1596704017254-9b121068fb31?w=800"
                alt="Luxury skincare"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div className="max-w-lg">
              <p className="text-sm uppercase tracking-widest text-gray-500 mb-4">The LUXE Experience</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">Elegance in Every Detail</h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                At LUXE, we believe that luxury is not just about products—it's about the entire experience. 
                From the moment you browse our collection to the carefully wrapped package that arrives at your door, 
                every touchpoint is designed to delight.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our buyers travel the world to source the finest cosmetics, jewelry, and fashion pieces, 
                ensuring that each item in our collection meets our exacting standards of quality and style.
              </p>
              <Link
                to="/shop"
                className="inline-block bg-luxury-charcoal text-white px-8 py-4 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors"
              >
                Discover More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl mb-2">{t('home.bestSellers')}</h2>
              <p className="text-gray-500">{t('home.heroSubtitle')}</p>
            </div>
            <Link
              to="/shop?bestSeller=true"
              className="hidden md:flex items-center space-x-2 text-sm uppercase tracking-widest hover:text-luxury-gold transition-colors"
            >
              <span>{t('common.viewAll')}</span>
              <HiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {bestSellers.slice(0, 4).map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-20">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">Follow @luxestore</h2>
          <p className="text-gray-500">Share your LUXE moments with us</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <a
              key={i}
              href="#"
              className="aspect-square overflow-hidden group"
            >
              <img
                src={`https://images.unsplash.com/photo-${
                  ['1596462502278-27bfdc403348', '1512496015851-a90fb38ba796', '1541643600914-78b084683601', 
                   '1515562141207-7a88fb7ce338', '1596704017254-9b121068fb31', '1617038260897-41a1f14a8ca0'][i - 1]
                }?w=400`}
                alt={`Instagram ${i}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
